import { Textarea } from '@/components/ui/FormControls';
import { useQueryClient } from '@tanstack/react-query';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import { useRef, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import type { Purchase } from '@/hooks/usePurchases';
import { useOperation, useOperations } from '@/hooks/useOperations';
import { useOperationIntent } from '@/hooks/useOperationIntent';
import { operationsRpc } from '@/lib/operations';
import WorkModalShell from '@/components/common/WorkModalShell';
import { purchaseInputClass as input } from './PurchasePaymentFields';
import { toast } from 'sonner';
type Action = 'delete' | 'restore' | 'archive' | 'unarchive';
export default function PurchaseLifecycleActions({ purchase: p }: { purchase: Purchase }) {
  const { can } = useAuth();
  const client = useQueryClient();
  const ops = useOperations();
  const mutate = useOperation('change_purchase_lifecycle_v1');
  const intent = useOperationIntent(p.id);
  const [action, setAction] = useState<Action | null>(null),
    [reason, setReason] = useState(''),
    [error, setError] = useState(''),
    [protectedMessage, setProtected] = useState('');
  const lock = useRef(false);
  const [preparing, setPreparing] = useState(false);
  const [version, setVersion] = useState(p.updated_at);
  async function open(next: Action) {
    if (lock.current) return;
    lock.current = true;
    setPreparing(true);
    setError('');
    setReason('');
    setProtected('');
    setAction(next);
    try {
      const preview = await operationsRpc<{
        version: string;
        can_delete: boolean;
        active_payment: boolean;
        stock_history: boolean;
      }>('preview_purchase_lifecycle_v1', { p_data: { purchase_id: p.id, action: next } });
      if (preview.version !== p.updated_at) {
        setProtected(
          'This purchase changed since it was loaded. Reload its details and review before continuing.',
        );
        return;
      }
      setVersion(preview.version);
      if (next === 'delete' && !preview.can_delete)
        setProtected(
          'This purchase has payment or stock history. Use Archive to hide it while preserving its financial history.',
        );
    } catch (e) {
      setProtected('The preview could not be loaded. Close and retry.');
      setError((e as Error).message);
    } finally {
      lock.current = false;
      setPreparing(false);
    }
  }
  async function submit() {
    if (!action || lock.current || protectedMessage) return;
    lock.current = true;
    setError('');
    try {
      await mutate.mutateAsync({
        purchase_id: p.id,
        expected_updated_at: version,
        action,
        reason,
        request_key: intent.key(`${action}:${version}`),
      });
      intent.complete(`${action}:${version}`);
      setAction(null);
      toast.success('Purchase updated. History retained.');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      lock.current = false;
    }
  }
  if (ops.data?.modules.purchases !== 1 || p.is_voided) return null;
  const actions: Action[] = p.is_deleted
    ? ['restore']
    : p.archived_at
      ? ['unarchive', 'delete']
      : ['archive', 'delete'];
  const labels = {
    delete: 'Delete',
    restore: 'Restore',
    archive: 'Archive',
    unarchive: 'Unarchive',
  };
  return (
    <>
      <div className="flex flex-wrap gap-2">
        {actions
          .filter((a) => can(`purchases.${a === 'unarchive' ? 'archive' : a}`))
          .map((a) => (
            <button
              key={a}
              onClick={() => void open(a)}
              disabled={preparing || mutate.isPending}
              className={`min-h-11 rounded-xl border px-3 text-sm ${a === 'delete' ? 'border-destructive/30 text-destructive' : 'border-border'}`}
            >
              {labels[a]}
            </button>
          ))}
      </div>
      {action && (
        <WorkModalShell
          open
          onClose={() => setAction(null)}
          busy={preparing || mutate.isPending}
          title={`${labels[action]} purchase`}
        >
          <h2 className="text-lg font-semibold">
            {labels[action]} {p.purchase_number}
          </h2>
          <div className="min-h-0 space-y-4 overflow-y-auto py-4">
            <p className="text-sm text-muted-foreground">
              {action === 'delete'
                ? 'Move this mistaken entry to the Recycle Bin. No record is permanently erased.'
                : action === 'archive'
                  ? 'Hide this genuine purchase from the active list. Its financial history remains included.'
                  : 'Restore visibility without posting payments or stock again.'}
            </p>
            {preparing ? (
              <p role="status">Checking linked history…</p>
            ) : protectedMessage ? (
              <p role="alert" className="rounded-xl bg-amber-500/10 p-3 text-sm">
                {protectedMessage}
              </p>
            ) : (
              <label className="block space-y-1 text-sm">
                Reason (3–500 characters)
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  className={input}
                  maxLength={500}
                  helpLabel="Reason (3–500 characters)"
                />
              </label>
            )}
            {error && (
              <p role="alert" className="text-sm text-destructive">
                {error}
              </p>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              className="min-h-11 rounded-xl border border-border px-4"
              onClick={() => setAction(null)}
              disabled={mutate.isPending}
            >
              Close
            </button>
            {error && (
              <button
                className="min-h-11 rounded-xl border border-border px-3"
                onClick={() =>
                  void intent
                    .recover(action)
                    .then((r) => {
                      if (r) {
                        setAction(null);
                        void refreshSaleConsumers(client);
                        toast.success('Previous update recovered.');
                      } else setError('No completed request found. Review and retry.');
                    })
                    .catch((e) => setError(e.message))
                }
              >
                Recover
              </button>
            )}
            <button
              disabled={
                preparing || mutate.isPending || !!protectedMessage || reason.trim().length < 3
              }
              className="min-h-11 rounded-xl bg-primary px-4 font-semibold text-primary-foreground disabled:opacity-50"
              onClick={() => void submit()}
            >
              {mutate.isPending ? 'Saving…' : labels[action]}
            </button>
          </div>
        </WorkModalShell>
      )}
    </>
  );
}
